import { Injectable } from '@angular/core'
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms'
import { GlobalMessageService, GlobalMessageType } from '@spartacus/core'
import {
  EbDependsOnConfig,
  EbFieldConfig,
  EbFormConfig,
  EbFormElementsConfig,
  EbFormGroupConfig,
  EbMultiFieldConfig
} from '../interfaces/eb-form.interface'
import { EbFormValidatorProviderService } from './eb-validator-provider.service'

type FormControls = { [key: string]: AbstractControl }

@Injectable({ providedIn: 'root' })
export class EbFormService {
  constructor(
    private readonly validatorProvider: EbFormValidatorProviderService,
    protected globalMessageService: GlobalMessageService
  ) {}

  private static getByteSizeFrom(size) {
    if (typeof size === 'number') {
      return size
    } else if (typeof size === 'string') {
      const [numStr, unit] = size.match(/(\d+)(\D+)/).splice(1)
      const num = parseInt(numStr, 10)
      switch (unit.toLowerCase()) {
        case 'kb':
          return num * 1024
        case 'mb':
          return num * 1048576
        case 'gb':
          return num * 1073741824
        case 'tb':
          return num * 1099511627776
        case 'pb':
          return num * 1125899906842624
        default:
          return num
      }
    } else {
      return null
    }
  }

  private static isFileAllowed(fieldConfig: EbFieldConfig, fileName: string) {
    const allowedFiles = fieldConfig.meta?.accept?.toLowerCase().split(/\s*,\s*/g)
    if (allowedFiles && allowedFiles.length > 0) {
      const regex = /(?:\.([^.]+))?$/
      const extension = regex.exec(fileName)
      if (extension) {
        for (const ext of allowedFiles) {
          if (ext === extension[0]?.toLowerCase()) {
            return true
          }
        }
      }
    } else {
      return true
    }
    return false
  }

  private static isFileSizeAllowed(fieldConfig: EbFieldConfig, size) {
    const maxFileSize = EbFormService.getByteSizeFrom(fieldConfig.meta?.maxFileSize)
    return !maxFileSize || size <= maxFileSize
  }

  public createSimpleDependsOn(
    controlPath: string,
    value: string,
    defaultAction = 'hide',
    conditionName = 'checkValue'
  ): EbDependsOnConfig {
    const dependsOn = {
      controls: [
        {
          controlPath,
          conditions: [
            {
              name: conditionName,
              arguments: [
                {
                  value
                }
              ]
            }
          ]
        }
      ]
    }
    dependsOn[defaultAction] = true
    return dependsOn
  }

  createFormByConfig(formConfig: EbFormConfig): UntypedFormGroup {
    return new UntypedFormGroup(this.processEbFormStepConfig(formConfig))
  }

  private processEbFormStepConfig(formConfig: EbFormConfig): FormControls {
    let controls: FormControls = {}
    formConfig.steps.forEach(step => {
      controls = { ...controls, ...this.processEbFormElementsConfig(step.elements) }
    })
    return controls
  }

  private processEbFormElementsConfig(elements: EbFormElementsConfig): FormControls {
    const controls: FormControls = {}
    for (const element of elements) {
      let control: AbstractControl = null
      if (element?.type === 'group') {
        control = this.processEbFormGroupConfig(element)
      }
      if (element?.type === 'field') {
        control = this.processEbFieldConfig(element)
      }
      if (control) {
        controls[element?.name] = control
      }
    }
    return controls
  }

  private processEbFormGroupConfig(group: EbFormGroupConfig): UntypedFormGroup {
    const controls = this.processEbFormElementsConfig(group.elements)
    const { validations, asyncValidations } = this.validatorProvider.processValidation(group)
    return new UntypedFormGroup(
      controls,
      (validations?.length && validations) || {},
      asyncValidations?.length && asyncValidations
    )
  }

  private processEbFieldConfig(field: EbFieldConfig): UntypedFormControl | UntypedFormArray {
    if (field.multiple && field.options?.length) {
      return this.processEbMultiFieldConfig(field as EbMultiFieldConfig)
    }
    const { validations, asyncValidations } = this.validatorProvider.processValidation(field)
    return new UntypedFormControl(
      field.value || '',
      (validations?.length && validations) || {},
      asyncValidations?.length && asyncValidations
    )
  }

  private processEbMultiFieldConfig(field: EbMultiFieldConfig): UntypedFormArray {
    const { validations, asyncValidations } = this.validatorProvider.processValidation(field)
    const formArray = new UntypedFormArray(
      [],
      (validations?.length && validations) || {},
      asyncValidations?.length && asyncValidations
    )
    if (field.value instanceof Array) {
      field.value.forEach(value => {
        if (field.options.find(option => option.name === value)) {
          formArray.push(new UntypedFormControl(value))
        }
      })
    }
    return formArray
  }

  toCamelCasePath(path: string): string {
    return path
      .split('.')
      .map(part => this.toCamelCaseString(part))
      .join('.')
  }

  toCamelCaseString(str: string): string {
    return str.replace(/\W+(\w?)/g, (a, b) => b.toUpperCase())
  }

  toDashedString(str: string): string {
    return str.replace(/([A-Z])/g, a => '-' + a.toLowerCase()).replace(/\W+/, '-')
  }

  checkFile(fieldConfig: EbFieldConfig, file: File) {
    if (!EbFormService.isFileAllowed(fieldConfig, file.name)) {
      this.globalMessageService.add(
        { key: 'formErrors.file.invalid.contentType' },
        GlobalMessageType.MSG_TYPE_ERROR
      )
      return false
    }
    if (!EbFormService.isFileSizeAllowed(fieldConfig, file.size)) {
      this.globalMessageService.add(
        { key: 'formErrors.file.invalid.size' },
        GlobalMessageType.MSG_TYPE_ERROR
      )
      return false
    }
    return true
  }
}
